import React, { useState } from "react";
import { Send, X, MessageSquare, Clock, DollarSign, Sparkles, CheckCircle2 } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import logger from "../../utils/logger.js";
import { getFreelancerName, getFreelancerServiceTitle } from "../../api/freelancerServices";
import { createOrGetConversation, sendConversationMessage } from "../../api/conversations";

export default function ContactSellerModal({ isOpen, onClose, freelancer, service }) {
  const { lang } = useLanguage();
  const { isLogged } = useAuth();
  const navigate = useNavigate();
  const isAr = lang === "ar";

  const [message, setMessage] = useState("");
  const [budget, setBudget] = useState("");
  const [timeline, setTimeline] = useState("");
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  if (!isOpen) return null;

  const freelancerName = getFreelancerName(freelancer, isAr);
  const avatarUrl = freelancer?.avatar_url || freelancer?.avatar || freelancer?.image;
  const serviceTitle = getFreelancerServiceTitle(service, isAr);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!message.trim()) return;

    if (!isLogged) {
      onClose();
      navigate("/login");
      return;
    }

    try {
      setSending(true);
      setError("");

      const formattedContent = [
        `📩 **${isAr ? "استفسار تواصل قبل الطلب" : "Pre-order Service Inquiry"}**`,
        `**${isAr ? "الخدمة:" : "Gig:"}** ${serviceTitle || "General Inquiry"}`,
        budget ? `**${isAr ? "الميزانية المتوقعة:" : "Estimated Budget:"}** $${budget}` : null,
        timeline ? `**${isAr ? "التسليم المتوقع:" : "Estimated Timeline:"}** ${timeline}` : null,
        `\n**${isAr ? "الرسالة:" : "Message:"}**\n${message.trim()}`,
      ]
        .filter(Boolean)
        .join("\n");

      const serviceId = service?.id;

      if (!serviceId) {
        setError(isAr ? "تعذر التحديد التلقائي لرمز الخدمة." : "Service ID could not be identified.");
        setSending(false);
        return;
      }

      const freelancerUserId =
        freelancer?.user_id ||
        freelancer?.freelancer_id ||
        freelancer?.id ||
        service?.user_id ||
        service?.freelancer_id ||
        service?.provider_id ||
        service?.freelancer?.user_id ||
        service?.freelancer?.id;

      const conversation = await createOrGetConversation(freelancerUserId, serviceId);
      if (conversation?.id) {
        await sendConversationMessage(conversation.id, { message: formattedContent });
      }

      setSending(false);
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        setMessage("");
        setBudget("");
        setTimeline("");
        onClose();
        navigate("/Account?tab=conversations");
      }, 1200);
    } catch (err) {
      logger.error("Failed to send seller inquiry", err);
      setSending(false);
      const apiMessage = err?.response?.data?.message || err?.message;
      setError(apiMessage || (isAr ? "تعذر إرسال الرسالة حاليًا. يرجى المحاولة لاحقًا." : "Failed to send inquiry. Please try again."));
    }
  }

  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center z-3 p-3"
      style={{ background: "rgba(15, 23, 42, 0.75)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
      dir={isAr ? "rtl" : "ltr"}
    >
      <div
        className="card border-0 shadow-lg rounded-4 overflow-hidden w-100 bg-body"
        style={{ maxWidth: "560px" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-success text-white p-4 d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-3">
            <div className="rounded-circle bg-white bg-opacity-20 d-flex align-items-center justify-content-center text-white font-bold" style={{ width: "46px", height: "46px" }}>
              {avatarUrl ? (
                <img src={avatarUrl} alt={freelancerName} className="w-100 h-100 rounded-circle object-fit-cover" />
              ) : (
                <MessageSquare size={22} />
              )}
            </div>
            <div>
              <h3 className="fw-bold fs-5 m-0 text-white">{isAr ? `التواصل مع ${freelancerName}` : `Contact ${freelancerName}`}</h3>
              <span className="small text-white text-opacity-80">{isAr ? "استفسار ومناقشة متطلبات الخدمة" : "Pre-order inquiry & custom quotes"}</span>
            </div>
          </div>
          <button type="button" className="btn text-white p-1 hover-opacity" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-4">
          {success ? (
            <div className="text-center py-4">
              <CheckCircle2 size={50} className="text-success mb-3" />
              <h4 className="fw-bold fs-5">{isAr ? "تم إرسال استفسارك بنجاح!" : "Inquiry Sent Successfully!"}</h4>
              <p className="text-muted small">{isAr ? "سيقوم المستقل بالرد عليك في أقرب وقت ممكن." : "The freelancer will respond to your message shortly."}</p>
            </div>
          ) : (
            <>
              {serviceTitle ? (
                <div className="p-3 mb-3 rounded-3 bg-body-tertiary border d-flex align-items-center gap-2">
                  <Sparkles size={16} className="text-success flex-shrink-0" />
                  <span className="small fw-semibold text-body truncate-1">
                    {isAr ? "الخدمة المستهدفة:" : "Related Gig:"} {serviceTitle}
                  </span>
                </div>
              ) : null}

              {error ? <div className="alert alert-danger small rounded-3 mb-3">{error}</div> : null}

              {/* Message Input */}
              <div className="mb-3">
                <label className="form-label small font-bold text-muted text-uppercase">
                  {isAr ? "تفاصيل طلبك أو استفسارك" : "Describe your request"} <span className="text-danger">*</span>
                </label>
                <textarea
                  className="form-control rounded-3 p-3 bg-body text-body"
                  rows={4}
                  placeholder={
                    isAr
                      ? "اشرح للمستقل تفاصيل المشروح، متطلبات التصميم أو البرمجة، والتسليم المطلوب..."
                      : "Describe your project scope, deliverables, or questions for the seller..."
                  }
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                />
              </div>

              {/* Optional Budget & Timeline */}
              <div className="row g-3 mb-4">
                <div className="col-6">
                  <label className="form-label small font-bold text-muted text-uppercase d-flex align-items-center gap-1">
                    <DollarSign size={14} className="text-success" />
                    {isAr ? "الميزانية المتوقعة ($)" : "Est. Budget ($)"}
                  </label>
                  <input
                    type="number"
                    className="form-control rounded-3 bg-body text-body"
                    placeholder="e.g. 150"
                    value={budget}
                    onChange={(e) => setBudget(e.target.value)}
                  />
                </div>
                <div className="col-6">
                  <label className="form-label small font-bold text-muted text-uppercase d-flex align-items-center gap-1">
                    <Clock size={14} className="text-success" />
                    {isAr ? "موعد التسليم" : "Timeframe"}
                  </label>
                  <input
                    type="text"
                    className="form-control rounded-3 bg-body text-body"
                    placeholder={isAr ? "مثال: 3 أيام" : "e.g. 3 days"}
                    value={timeline}
                    onChange={(e) => setTimeline(e.target.value)}
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="d-flex align-items-center gap-2 justify-content-end pt-2 border-top">
                <button type="button" className="btn btn-outline-secondary rounded-3 px-4 fw-semibold" onClick={onClose}>
                  {isAr ? "إلغاء" : "Cancel"}
                </button>
                <button
                  type="submit"
                  className="fv-search-box__btn rounded-3 px-4 py-2"
                  disabled={sending || !message.trim()}
                >
                  {sending ? (
                    <span>{isAr ? "جاري الإرسال..." : "Sending..."}</span>
                  ) : (
                    <>
                      <span>{isAr ? "إرسال الرسالة" : "Send Inquiry"}</span>
                      <Send size={16} className={isAr ? "rotate-180" : ""} />
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
